import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Plus,
  Search,
  PhoneOutgoing,
  PhoneIncoming,
  PlayCircle,
} from 'lucide-react';
import { ScriptCard } from '@/components/ScriptCard';
import { TemplateBrowser } from '@/components/TemplateBrowser';

import { useOptimizedSupabaseScripts } from '@/hooks/useOptimizedSupabaseScripts';
import { useInboundFeatureFlag } from '@/hooks/useInboundFeatureFlag';
import { cn } from '@/lib/utils';
import { TrainingVideoModal } from '@/components/TrainingVideoModal';
import { useLocationTrainingVideo } from '@/hooks/useLocationTrainingVideo';

export default function ScriptManagement() {
  const { scripts, addScript, updateScript, deleteScript, refreshScripts } =
    useOptimizedSupabaseScripts();
  const { enableInboundMode } = useInboundFeatureFlag();
  const { hasVideo, displayTitle } = useLocationTrainingVideo('scripts');
  const [searchTerm, setSearchTerm] = useState('');
  const [modeFilter, setModeFilter] = useState<'all' | 'outbound' | 'inbound'>(
    'all'
  );
  const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
  const [editingScript, setEditingScript] = useState<any | null>(null);

  const filteredScripts = scripts.filter((script) => {
    const matchesSearch =
      script.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (script.target_industry || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (script.item_sold || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMode = modeFilter === 'all' || script.mode === modeFilter;
    return matchesSearch && matchesMode;
  });
  const outboundCount = scripts.filter((s) => s.mode === 'outbound').length;
  const inboundCount = scripts.filter((s) => s.mode === 'inbound').length;
  const handleEdit = async (script: any) => {
    await refreshScripts?.(); // ← this is the magic
    setEditingScript(script);
    setShowTemplateBrowser(true);
  };
  const handleDuplicate = (script: any) => {
    // Remove database-specific fields and add copy suffix
    const { id, created_at, updated_at, user_id, ...scriptData } = script;

    const duplicatedScript = {
      ...scriptData,
      name: `${script.name} (Copy)`,
    };

    addScript(duplicatedScript);
  };
  const handleCloseCreator = () => {
    setShowTemplateBrowser(false);
    setEditingScript(null);
  };

  const handleCreateScript = () => {
    setShowTemplateBrowser(true);
  };

  const handleHeaderCreateClick = () => {
    handleCreateScript();
  };

  const handleScriptCreated = async () => {
    setShowTemplateBrowser(false);
    setEditingScript(null);
    await refreshScripts?.();
  };

  return (
    <div className='container mx-auto p-6 space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Script Management</h1>
          <p className='text-muted-foreground'>
            Create, manage, and organize your sales scripts
          </p>
        </div>

        {hasVideo && (
          <div className='mb-6'>
            <TrainingVideoModal
              trigger={
                <Button variant='outline' size='sm'>
                  <PlayCircle className='h-4 w-4 mr-2' />
                  {displayTitle || 'View Training'}
                </Button>
              }
              locationKey='scripts'
            />
          </div>
        )}
        <div className='flex items-center gap-3'>
          <Button onClick={handleHeaderCreateClick} className='gap-2'>
            <Plus className='h-4 w-4' />
            Create New Script
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle className='text-lg'>Search & Filter</CardTitle>
            <div className='flex gap-2'>
              <Button
                variant={modeFilter === 'all' ? 'default' : 'outline'}
                size='sm'
                onClick={() => setModeFilter('all')}
                className='gap-1.5'
              >
                All Scripts
                <Badge variant='secondary' className='ml-1'>
                  {scripts.length}
                </Badge>
              </Button>
              <Button
                variant={modeFilter === 'outbound' ? 'default' : 'outline'}
                size='sm'
                onClick={() => setModeFilter('outbound')}
                className='gap-1.5'
              >
                <PhoneOutgoing className='h-3 w-3' />
                Outbound
                <Badge variant='secondary' className='ml-1'>
                  {outboundCount}
                </Badge>
              </Button>
              {enableInboundMode && (
                <Button
                  variant={modeFilter === 'inbound' ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => setModeFilter('inbound')}
                  className='gap-1.5'
                >
                  <PhoneIncoming className='h-3 w-3' />
                  Inbound
                  <Badge variant='secondary' className='ml-1'>
                    {inboundCount}
                  </Badge>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className='flex-1 relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder='Search scripts by name, industry, or offer...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='pl-10'
            />
          </div>
        </CardContent>
      </Card>

      {/* Scripts Grid */}
      {filteredScripts.length > 0 ? (
        <div
          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          key={scripts
            .map((s) => s.id + (s.updated_at || s.created_at))
            .join('-')}
        >
          {filteredScripts.map((script) => (
            <ScriptCard
              key={script.id}
              script={script}
              onEdit={handleEdit}
              onDelete={deleteScript}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <div className='text-center space-y-4'>
              <div
                className={cn(
                  'w-16 h-16 rounded-full flex items-center justify-center mx-auto',
                  modeFilter === 'outbound'
                    ? 'bg-primary/10'
                    : modeFilter === 'inbound' && enableInboundMode
                    ? 'bg-success/10'
                    : 'bg-muted'
                )}
              >
                {modeFilter === 'outbound' ? (
                  <PhoneOutgoing className='h-8 w-8 text-primary' />
                ) : modeFilter === 'inbound' && enableInboundMode ? (
                  <PhoneIncoming className='h-8 w-8 text-success' />
                ) : (
                  <Plus className='h-8 w-8 text-muted-foreground' />
                )}
              </div>
              <div>
                <h3 className='text-lg font-semibold'>
                  {scripts.length === 0
                    ? 'No scripts yet'
                    : modeFilter === 'outbound' && outboundCount === 0
                    ? 'No outbound scripts yet'
                    : modeFilter === 'inbound' &&
                      inboundCount === 0 &&
                      enableInboundMode
                    ? 'No inbound scripts yet'
                    : 'No scripts match your search'}
                </h3>
                <p className='text-muted-foreground'>
                  {scripts.length === 0
                    ? 'Create your first script to get started'
                    : modeFilter === 'outbound' && outboundCount === 0
                    ? 'Create your first outbound script for making cold calls'
                    : modeFilter === 'inbound' &&
                      inboundCount === 0 &&
                      enableInboundMode
                    ? 'Create your first inbound script for handling incoming calls'
                    : 'Try adjusting your search or filters'}
                </p>
              </div>
              {scripts.length === 0 ? (
                <div className='flex gap-2'>
                  <Button onClick={handleCreateScript} className='gap-2'>
                    <Plus className='h-4 w-4' />
                    Create Script
                  </Button>
                </div>
              ) : modeFilter === 'outbound' && outboundCount === 0 ? (
                <Button onClick={handleCreateScript} className='gap-2'>
                  <PhoneOutgoing className='h-4 w-4' />
                  Create Outbound Script
                </Button>
              ) : modeFilter === 'inbound' &&
                inboundCount === 0 &&
                enableInboundMode ? (
                <Button onClick={handleCreateScript} className='gap-2'>
                  <PhoneIncoming className='h-4 w-4' />
                  Create Inbound Script
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Browser Dialog */}
      <TemplateBrowser
        key={editingScript?.id || 'new'}
        open={showTemplateBrowser}
        onOpenChange={(open) => {
          setShowTemplateBrowser(open);
          if (!open) {
            setEditingScript(null);
          }
        }}
        editingScript={editingScript}
        onScriptCreated={handleScriptCreated}
      />
    </div>
  );
}
